import {
    Box,
    DialogActions,
    DialogContent,
    Divider, Link,
    Paper,
    Stack,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from "@mui/material";
import {LoadingContent} from "../LoadingContent";
import React, {useState} from "react";
import {AccountResponse} from "../../data/AccountResponse";
import useApiCall from "../../hooks/CancellableApiCall";
import {InactivityNoticeResponse} from "../../data/InactivityNoticeResponse";
import {formatDate} from "../../utils";
import {PoliceUser} from "../PoliceUser";
import CreateInactivityNoticeForm from "./CreateInactivityNoticeForm";
import EditActivityNoticeForm from "./EditActivityNoticeForm";
import DeleteActivityNoticePrompt from "./DeleteActivityNoticePrompt";
import {OfficerDivisionPlaytimeResponse} from "../../data/OfficerPlayTimeSummaryResponse";
import {TabContext, TabList, TabPanel} from "@mui/lab";
import {InactivityNoticeApprovalResponse} from "../../data/InactivityNoticeApprovalResponse";
import HelpOutlineTwoToneIcon from '@mui/icons-material/HelpOutlineTwoTone';
import Dialog from "@mui/material/Dialog";
import Button from "@mui/material/Button";
import {Link as RouterLink} from "react-router-dom";
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

interface EmployeeActivityProps {
    communityId: string,
    self: AccountResponse,
    isOwn: boolean
}

interface EmployeeActivityContentProps extends EmployeeActivityProps {
    notices: InactivityNoticeResponse[],
    activity: OfficerDivisionPlaytimeResponse[]
}

interface ActivityApprovalProps {
    data: InactivityNoticeApprovalResponse
}

function ActivityApproval(props: ActivityApprovalProps) {
    let icon = <></>
    if (props.data.approved === null) {
        icon = <HelpOutlineTwoToneIcon color={"warning"} />
    }
    if (props.data.approved === false) {
        icon = <HighlightOffIcon color={"error"} />
    }
    if (props.data.approved === true) {
        icon = <TaskAltIcon color={"success"} />
    }

    return <Stack direction={"row"} spacing={1}>
        {icon}
        <Typography>{props.data.divisionDisplayName} - {props.data.approved === null ? "Pending Review" :
            <>{props.data.approved ? "Approved" : "Denied"} by <Link underline="none" component={RouterLink} to={`/user/${props.data.handlerCommunityId}`}>
                {props.data.handlerNick}
            </Link>
            </>}</Typography>
    </Stack>
}

type ActivityApprovalPanelType = "pending" | "denied" | "approved"

function ActivityApprovalPanel(props: { notices?: InactivityNoticeApprovalResponse[], type: ActivityApprovalPanelType}) {
    const [open, setOpen] = useState(false)

    const handleClose = () => {
        setOpen(false);
    };

    let buttonType
    if (props.type === "pending") {
        buttonType = <HelpOutlineTwoToneIcon color={"warning"} onClick={() => setOpen(true)}/>
    }
    if (props.type === "denied") {
        buttonType = <HelpOutlineTwoToneIcon color={"error"} onClick={() => setOpen(true)}/>
    }
    if (props.type === "approved") {
        buttonType = <HelpOutlineTwoToneIcon color={"success"} onClick={() => setOpen(true)}/>
    }

    return <React.Fragment>
        {buttonType}
        <Dialog open={open} onClose={handleClose}>
            <DialogContent>Approvals</DialogContent>
            <DialogContent>
                {
                    props.notices && props.notices.map(notice => (
                        <ActivityApproval key={notice.id} data={notice}/>
                    ))
                }
            </DialogContent>
            <DialogActions>
                <Button color={"info"} onClick={() => {setOpen(false)}}>Close</Button>
            </DialogActions>
        </Dialog>
    </React.Fragment>
}

function ActivityRow(props: { notice: InactivityNoticeResponse, self: AccountResponse, isOwn: boolean }) {
    let activityApprovalText

    if (props.notice.approvals?.length === 0) {
        activityApprovalText = "No Approvals Required"
    } else {
        if (props.notice.approvals?.find((a) => {
            return a.approved === null
        }) !== undefined) {
            activityApprovalText = <Stack spacing={1} direction={"row"}>
                <ActivityApprovalPanel notices={props.notice.approvals} type={"pending"}/>
                Pending Approvals
            </Stack>
        } else {
            if (props.notice.approvals?.find((a) => {
                return a.approved === false
            }) !== undefined) {
                activityApprovalText = <Stack spacing={1} direction={"row"}>
                    <ActivityApprovalPanel notices={props.notice.approvals} type={"denied"}/>
                    Denied
                </Stack>
            } else {
                activityApprovalText = <Stack spacing={1} direction={"row"}>
                    <ActivityApprovalPanel notices={props.notice.approvals} type={"approved"}/>
                    Approved
                </Stack>
            }
        }
    }

    return <TableRow>
        <TableCell> {props.notice.note} </TableCell>
        <TableCell> {formatDate(props.notice.startDate)} </TableCell>
        <TableCell> {formatDate(props.notice.endDate)} </TableCell>
        <TableCell> {props.notice.issuerNick && props.notice.issuerCommunityId ?
            <PoliceUser user={{
                nick: props.notice.issuerNick,
                communityId: props.notice.issuerCommunityId
            }}/> : <></>} </TableCell>
        <TableCell>
            {activityApprovalText}
        </TableCell>
        <TableCell>
            {props.self.permissions.includes("inactivityNotice:edit") || props.isOwn ?
                <EditActivityNoticeForm id={props.notice.id} content={props.notice.note} startDate={props.notice.startDate} endDate={props.notice.endDate}
                                        communityId={props.notice.employeeCommunityId}/> : <></>
            }
            {props.self.permissions.includes("inactivityNotice:delete") ?
                <DeleteActivityNoticePrompt id={props.notice.id}
                                            communityId={props.notice.employeeCommunityId}/> : <></>
            }
        </TableCell>
    </TableRow>
}


interface EmployeeActivityTableProps {
    divisionPlaytime: OfficerDivisionPlaytimeResponse
}

function EmployeeActivityTable(props: EmployeeActivityTableProps) {
    return <TableContainer component={Paper}>
        <Table size="small">
            <TableHead>
                <TableRow>
                    <TableCell>Start Date</TableCell>
                    <TableCell>Duration</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {props.divisionPlaytime.playtimeEntries.map((entry) => {
                    const date = new Date(entry.date).toLocaleDateString("en-GB", {
                        year: 'numeric',
                        month: 'long',
                        timeZone: 'UTC'
                    })
                    let minutes = Math.floor( (entry.playtime / 60) % 60).toString()
                    if (minutes.length <= 1) {
                        minutes = "0" + minutes
                    }
                    const hours = Math.floor(entry.playtime / 3600)
                    return <TableRow key={entry.date}>
                        <TableCell>{date}</TableCell>
                        <TableCell>{`${hours}h ${minutes}m`}</TableCell>
                    </TableRow>
                })}
            </TableBody>
        </Table>
    </TableContainer>
}

function EmployeeActivityContent(props: EmployeeActivityContentProps) {
    const [selectedTab, setSelectedTab] = useState(props.activity[0]?.divisionIdentifier)

    return <Stack spacing={2}>
        {props.self.permissions.includes("inactivityNotice:create") || props.communityId === props.self.communityId ? <Typography variant={"h6"}>Inactivity Notices <CreateInactivityNoticeForm communityId={props.communityId}/></Typography> : <><Typography variant={"h6"}>Inactivity Notices</Typography></>}
        <Box sx={{overflowX:"auto"}}>
        <Table>
            <TableHead>
                <TableRow>
                    <TableCell>Note</TableCell>
                    <TableCell>Start Date</TableCell>
                    <TableCell>End Date</TableCell>
                    <TableCell>Issuer</TableCell>
                    <TableCell>Approval Status</TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {
                    props.notices?.map(notice => {
                            return <ActivityRow notice={notice} self={props.self} key={notice.id} isOwn={props.isOwn}/>
                        }
                    )
                }
            </TableBody>
        </Table>
        </Box>
        <Divider/>
        <Typography variant={"h6"}>Playtime By Division</Typography>
        <TabContext value={selectedTab}>
            <>
                <TabList variant="scrollable" onChange={(_, newValue) => setSelectedTab(newValue)}>
                    {props.activity && props.activity.map(divisionPlaytime => {
                        return <Tab key={divisionPlaytime.divisionIdentifier} value={divisionPlaytime.divisionIdentifier} label={divisionPlaytime.divisionName}/>
                    })}
                </TabList>
                {props.activity && props.activity.map(divisionPlaytime => {
                    return <TabPanel key={divisionPlaytime.divisionIdentifier} value={divisionPlaytime.divisionIdentifier}>
                        <EmployeeActivityTable divisionPlaytime={divisionPlaytime}></EmployeeActivityTable>
                    </TabPanel>
                })}
            </>
        </TabContext>


    </Stack>

}

export const EmployeeActivity = (props: EmployeeActivityProps) => {
    const notices = useApiCall<InactivityNoticeResponse[]>({
        url: `/api/activity/${props.communityId}`,
    })

    const activity = useApiCall<OfficerDivisionPlaytimeResponse[]>({
        url: `/api/activity/${props.communityId}/monthly-activity`,
    })

    return <LoadingContent isLoading={
        notices.isLoading || !notices ||
        activity.isLoading || !activity
    }>
        <EmployeeActivityContent notices={notices.data!!} communityId={props.communityId} self={props.self}
                                 isOwn={props.isOwn} activity={activity.data!!}/>
    </LoadingContent>
}